/**
 * Castle VISUAL layout (sprite position + scale per segment), separate from the gameplay
 * anchors (mapConfig.aiFortressX / playerCastleX, which drive unit paths/spawns/collisions
 * and must never move). The defaults below match the original on-map positions; an override
 * saved by the in-game castle-edit tool (?castleedit=1) is merged on top so a hand-placed
 * layout sticks and can later be baked in as the new default.
 */
export type CastleTransform = { x: number; y: number; scale: number };
export type SideLayout = { top: CastleTransform; main: CastleTransform; bottom: CastleTransform };
export type CastleLayout = { ai: SideLayout; player: SideLayout };

// Hand-placed by the owner with the in-game castle-edit tool (?castleedit=1) so the sprites
// line up with the keeps painted into the background map.
export const DEFAULT_CASTLE_LAYOUT: CastleLayout = {
  ai: {
    top: { x: 35, y: 87, scale: 0.43 },
    main: { x: 32, y: 315, scale: 0.62 },
    bottom: { x: 39, y: 522, scale: 0.43 }
  },
  player: {
    top: { x: 1162, y: 114, scale: 0.43 },
    main: { x: 1162, y: 321, scale: 0.62 },
    bottom: { x: 1162, y: 462, scale: 0.43 }
  }
};

const STORAGE_KEY = "DSAI_CASTLE_LAYOUT";

function mergeTransform(base: CastleTransform, override?: Partial<CastleTransform>): CastleTransform {
  return {
    x: typeof override?.x === "number" ? override.x : base.x,
    y: typeof override?.y === "number" ? override.y : base.y,
    scale: typeof override?.scale === "number" ? override.scale : base.scale
  };
}

function mergeSide(base: SideLayout, override?: Partial<SideLayout>): SideLayout {
  return {
    top: mergeTransform(base.top, override?.top),
    main: mergeTransform(base.main, override?.main),
    bottom: mergeTransform(base.bottom, override?.bottom)
  };
}

/** Current castle layout: the default with any saved override merged on top. */
export function loadCastleLayout(): CastleLayout {
  let saved: Partial<CastleLayout> | undefined;
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    saved = raw ? (JSON.parse(raw) as Partial<CastleLayout>) : undefined;
  } catch {
    saved = undefined;
  }
  return {
    ai: mergeSide(DEFAULT_CASTLE_LAYOUT.ai, saved?.ai),
    player: mergeSide(DEFAULT_CASTLE_LAYOUT.player, saved?.player)
  };
}
